// PeakEdu Tier 0 WASM — Plausible Analytics scaffold (B29 SD44 PM)
//
// Privacy-friendly analytics — no cookie cross-site, no fingerprint, GDPR/ePrivacy compliant.
// Reference: docs/legal/cookie-policy-vn.md § 2.2 + docs/runbooks/analytics-plausible.md (B29)
//
// Activate flow:
//   1. Boss tạo Plausible account (cloud $9/mo OR self-host CF Workers free)
//   2. Add site `app.peakedu.vn` Plausible dashboard → copy domain
//   3. Set VITE_PLAUSIBLE_DOMAIN env var
//   4. Re-deploy WASM Tier 0
//
// Privacy gate:
//   - Plausible chỉ load khi cookie consent.choices.analytics === true
//   - Nếu user opt-out → Plausible KHÔNG load → 0 tracking
//
// Integration với cookie-consent.js:
//   - initCookieConsent onChange callback → reload page hoặc dynamic init
//   - Default OFF — user phải explicit accept analytics cookie consent banner
//
// Public API:
//   initPlausible({domain, source})         — load script + init
//   trackEvent(eventName, props)            — manual event tracking
//   getPlausibleEnabled()                   — check active state

const SCRIPT_BASE_URL = 'https://plausible.io/js/script.js';
// Self-host alternative: 'https://stats.peakedu.vn/js/script.js' nếu CF Workers self-host

let _loaded = false;
let _enabled = false;
let _domain = null;

/**
 * Load Plausible script tag dynamically với SRI nếu pinned.
 * Skip silent nếu domain unset hoặc consent OFF.
 *
 * @param {object} opts
 * @param {string} opts.domain  — VITE_PLAUSIBLE_DOMAIN (vd "app.peakedu.vn")
 * @param {string} [opts.source] — Override script URL (cho self-host)
 * @param {boolean} [opts.consentRequired] — default true; false để skip cookie gate (NOT recommended)
 * @returns {Promise<{enabled: boolean, error?: string}>}
 */
export async function initPlausible({ domain, source, consentRequired = true } = {}) {
  if (!domain) {
    return { enabled: false, error: 'no_domain' };
  }

  // Cookie consent gate
  if (consentRequired) {
    let consent;
    try {
      const raw = localStorage.getItem('peakedu_consent');
      consent = raw ? JSON.parse(raw) : null;
    } catch {
      return { enabled: false, error: 'consent_read_fail' };
    }
    if (!consent?.choices?.analytics) {
      return { enabled: false, error: 'consent_analytics_off' };
    }
  }

  if (_loaded) {
    return { enabled: _enabled };
  }

  return new Promise((resolve) => {
    const script = document.createElement('script');
    const scriptSrc = source || SCRIPT_BASE_URL;
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-domain', domain);
    // Honor Do Not Track tự động qua Plausible
    script.addEventListener('load', () => {
      _loaded = true;
      _enabled = true;
      _domain = domain;
      // Plausible exposes window.plausible() global
      if (typeof window.plausible !== 'function') {
        // Some script versions expose differently — fallback queue pattern
        window.plausible = window.plausible || function() {
          (window.plausible.q = window.plausible.q || []).push(arguments);
        };
      }
      resolve({ enabled: true });
    });
    script.addEventListener('error', () => {
      _loaded = true;
      _enabled = false;
      resolve({ enabled: false, error: 'script_load_fail' });
    });
    document.head.appendChild(script);
  });
}

/**
 * Track custom event qua Plausible.
 * Skip silent nếu Plausible chưa init.
 *
 * @param {string} eventName  — vd "test_completed", "experiment_viewed"
 * @param {object} [props]    — Properties (max ~30 chars value, ~5 keys best practice)
 */
export function trackEvent(eventName, props) {
  if (!_enabled || typeof window.plausible !== 'function') {
    return;
  }
  try {
    if (props) {
      window.plausible(eventName, { props });
    } else {
      window.plausible(eventName);
    }
  } catch {
    // Fail-open
  }
}

/**
 * Check Plausible active state (ví dụ disable feature dependent on analytics).
 */
export function getPlausibleEnabled() {
  return _enabled;
}

/**
 * Re-init khi user thay đổi consent (vd accept analytics sau lần đầu reject).
 * Reload page là approach đơn giản, alternative: dynamic load.
 */
export function reinitOnConsentChange() {
  // Easiest path: reload — Plausible script tag re-evaluate
  window.location.reload();
}

export default {
  init: initPlausible,
  track: trackEvent,
  enabled: getPlausibleEnabled,
};
